import { Head, Link } from "@inertiajs/react"
import { ChevronRight, Search } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  formatAdminDate,
  LEAD_KIND_LABELS,
  LEAD_STATUS_LABELS,
  type AdminLeadSummary,
  type LeadKind,
  type LeadStatus,
} from "@/types/lead"

type Props = {
  leads: AdminLeadSummary[]
  filters: { query: string; kind: string; status: string }
  kind_options: LeadKind[]
  status_options: LeadStatus[]
}

const DESCRIPTION = "Search, filter, and triage Leads for Website Reviews and Consultation Requests, response deadlines, and current statuses in Zmile's private admin area."

export default function AdminLeadsIndex({ leads, filters, kind_options, status_options }: Props) {
  return (
    <>
      <Head title="Leads">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Leads" />
        <meta property="og:description" content={DESCRIPTION} />
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <AdminShell>
        <header className="border-b border-hairline pb-6">
          <h1>Leads</h1>
          <p className="mt-1">{leads.length} matching {leads.length === 1 ? "Lead" : "Leads"}, newest first.</p>
        </header>

        <form action="/admin/leads" className="mt-6 grid gap-3 rounded-md border border-hairline bg-surface p-4 md:grid-cols-[minmax(0,1fr)_180px_180px_auto]" method="get">
          <div className="space-y-2">
            <label className="sr-only" htmlFor="query">Search Leads</label>
            <Input defaultValue={filters.query} id="query" name="query" placeholder="Search name, email, company, or website" type="search" />
          </div>
          <div>
            <label className="sr-only" htmlFor="kind">Lead Kind</label>
            <Select defaultValue={filters.kind} id="kind" name="kind">
              <option value="">All kinds</option>
              {kind_options.map((kind) => <option key={kind} value={kind}>{LEAD_KIND_LABELS[kind]}</option>)}
            </Select>
          </div>
          <div>
            <label className="sr-only" htmlFor="status">Lead Status</label>
            <Select defaultValue={filters.status} id="status" name="status">
              <option value="">All statuses</option>
              {status_options.map((status) => <option key={status} value={status}>{LEAD_STATUS_LABELS[status]}</option>)}
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" type="submit"><Search aria-hidden="true" className="h-4 w-4" />Filter</Button>
            <Button asChild size="sm" variant="ghost"><Link href="/admin/leads">Clear</Link></Button>
          </div>
        </form>

        {leads.length > 0 ? (
          <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-md border border-hairline bg-page">
            {leads.map((lead) => (
              <li key={lead.id}>
                <Link className="grid gap-3 px-4 py-4 no-underline hover:bg-surface md:grid-cols-[minmax(0,1fr)_auto] md:items-center" href={`/admin/leads/${lead.id}`}>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <strong className="truncate">{lead.company_name}</strong>
                      <Badge tone={lead.kind === "website_review" ? "accent" : "neutral"}>{LEAD_KIND_LABELS[lead.kind]}</Badge>
                      <Badge tone="muted">{LEAD_STATUS_LABELS[lead.status]}</Badge>
                      {lead.response_deadline_state === "due" && <Badge tone="signal">Due soon</Badge>}
                      {lead.response_deadline_state === "overdue" && <Badge tone="danger">Overdue</Badge>}
                    </div>
                    <p className="mt-1 truncate">{lead.contact_name} · {lead.email}</p>
                    <div className="mt-1">
                      <small>
                        Received {formatAdminDate(lead.created_at)}
                        {lead.response_deadline && <> · Response deadline {formatAdminDate(lead.response_deadline)}</>}
                        {lead.requested_start_at && <> · Requested {formatAdminDate(lead.requested_start_at)}</>}
                      </small>
                    </div>
                  </div>
                  <ChevronRight aria-hidden="true" className="h-4 w-4 text-ink-muted" />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-6 rounded-md border border-dashed border-hairline p-8 text-center">
            <h2>No matching Leads</h2>
            <p className="mt-2">Adjust the search or filters to see another part of the inbox.</p>
          </div>
        )}
      </AdminShell>
    </>
  )
}
